import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { useAuth } from "./authContext";
import Loading from "./components/Loading";
import Button from "./components/Button";
import { AlertError, AlertSuccess } from "./components/Alert";

const API = process.env.NODE_ENV === 'development' ? "http://localhost:8080/api" : "/api"

interface InviteInfo {
    email: string
    company_name: string
    role_name: string
    expires_at: string
}

function InvitePage() {
    const { token } = useParams<{ token: string }>()
    const navigate = useNavigate()
    const { session, refreshProfile } = useAuth()
    const [invite, setInvite] = useState<InviteInfo | null>(null)
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(true)
    const [accepting, setAccepting] = useState(false)

    useEffect(() => {
        if (!token) return
        fetch(API + "/v1/public/invitations/" + token)
            .then(res => res.json())
            .then(data => {
                if (data.result === "success") {
                    setInvite(data.invitation)
                } else {
                    setError(data.message || "ไม่พบคำเชิญ")
                }
            })
            .catch(() => setError("เกิดข้อผิดพลาดในการโหลดข้อมูล"))
            .finally(() => setLoading(false))
    }, [token])

    async function handleLogin() {
        localStorage.setItem("invite_redirect", window.location.pathname)
        navigate("/login")
    }

    async function handleAccept() {
        if (!token) return
        setAccepting(true)
        try {
            const { data: { session } } = await supabase.auth.getSession()
            const res = await fetch(API + "/v1/invitations/accept-by-token", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({ token }),
            })
            const data = await res.json()
            if (data.result === "success") {
                AlertSuccess("ตอบรับคำเชิญสำเร็จ!")
                await refreshProfile()
                navigate("/home")
            } else {
                AlertError(data.message || "เกิดข้อผิดพลาด")
            }
        } catch {
            AlertError("เกิดข้อผิดพลาดในการเชื่อมต่อ")
        }
        setAccepting(false)
    }

    if (loading) return <Loading isLoading={true} />

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <Loading isLoading={accepting} />
            <div className="w-full max-w-md">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8 p-6 sm:p-8">
                    <h5 className="text-center text-lg font-semibold text-gray-900 mb-4">คำเชิญเข้าร่วมบริษัท</h5>

                    {error && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center rounded-lg px-4 py-3 mb-4">
                            {error}
                        </div>
                    )}

                    {invite && (
                        <div className="text-center">
                            <p className="text-sm text-gray-500 mb-1">คุณได้รับเชิญเข้าร่วม</p>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{invite.company_name}</h4>
                            <p className="text-sm text-gray-500 mb-1">
                                ในฐานะ <span className="inline-block bg-sky-100 text-sky-800 text-xs font-medium px-2 py-0.5 rounded">{invite.role_name}</span>
                            </p>
                            <small className="text-gray-400 block mb-6">หมดอายุ: {invite.expires_at}</small>

                            {session ? (
                                <Button
                                    variant="success"
                                    size="full"
                                    onClick={handleAccept}
                                >
                                    ยอมรับคำเชิญ
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="full"
                                    onClick={handleLogin}
                                >
                                    เข้าสู่ระบบ / สมัครสมาชิกเพื่อตอบรับ
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default InvitePage
